"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { assessmentCompletedOptions, residentTypeOptions, residenceTypeOptions, structureTypeOptions, canBeHomeRepairedOptions, isNewHousingHelpNeededOptions, buildingHeightOptions, potentialStructuralProblemsOptions, heightOfWaterOptions } from "../../constants/options"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import FormPhoneInput from "@/shared/components/form-inputs/form-phone-input"
import FormYesNoInput from "../../../../shared/components/form-inputs/form-yes-no-input"
import FormRadioInput from "../../../../shared/components/form-inputs/form-radio-input"
import { SchemaType } from "@/shared/types/helpers.type"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext } from "react-hook-form"
import { yesNoOptions } from "../../../../shared/constants/options"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"

const baseFieldName = "home.repairabilityInfo"

function HomeTypeForm() {
    const { control } = useFormContext<HomeWithHomeOwnerFormType>()
    const { session } = useSession()
    const isHomeowner = session?.role === UserRole.HOMEOWNER

    // Define all field names in this form section
    const fieldNames = [
        "home.repairabilityInfo.assessorName",
        "home.repairabilityInfo.structureType",
        "home.repairabilityInfo.residenceType",
        "home.repairabilityInfo.ownerNameIfRental",
        "home.repairabilityInfo.ownerPhoneNumberIfRental",
        "home.repairabilityInfo.canBeRepaired",
        "home.repairabilityInfo.newHousingHelpNeeded",
        "home.repairabilityInfo.rebuildingHelpNeeded",
        "home.repairabilityInfo.rebuildingSuppliesHelpNeeded",
        "home.repairabilityInfo.heightOfWater",
        "home.dogsInHome",
        "home.shedNeeded",
        "home.repairabilityInfo.structuralProblemDescription",
        "home.repairabilityInfo.buildingHeight",
        "home.repairabilityInfo.affectedSquareFeet",
        "home.repairabilityInfo.potentialStructuralProblems",
        "home.repairabilityInfo.assessmentCompleted"
    ]
    
    return (
        <HomeDetailsWrapperAccordion heading="Home Type & Repairability Info" fieldNames={fieldNames}>
            {/* row 1 */}
            <div className="flex flex-col gap-2">
                <FormTextInput
                    inputGroupProps={{
                        label: "Assessor's Name",
                    }}
                    placeholder="Assessor's Name"
                    name={`${baseFieldName}.assessorName`}
                    control={control}
                    disabled={isHomeowner}
                />
            </div>
            <FormSelectInput
                inputGroupProps={{
                    label: "Assessment Status",
                    wrapperClassName: "xl:col-span-3"
                }}
                options={assessmentCompletedOptions}
                name={`${baseFieldName}.assessmentCompleted`}
                control={control}
                disabled={isHomeowner}
            />
            <FormSelectInput
                inputGroupProps={{
                    label: "Structure Type",
                }}
                options={structureTypeOptions}
                name={`${baseFieldName}.structureType`}
                control={control}
            />
            <FormSelectInput
                inputGroupProps={{
                    label: "Residence Type",
                }}
                options={residenceTypeOptions}
                name={`${baseFieldName}.residenceType`}
                control={control}
            />
            {/* row 3 */}
            <FormTextInput
                inputGroupProps={{
                    label: "Owner Name (if rental)",
                }}
                placeholder="Owner Name"
                name={`${baseFieldName}.ownerNameIfRental`}
                control={control}
            />
            <FormPhoneInput
                inputGroupProps={{
                    label: "Owner Phone Number (if rental)",
                }}
                placeholder="Owner Phone Number"
                name={`${baseFieldName}.ownerPhoneNumberIfRental`}
                type={SchemaType.phone}
                control={control}
            />

            {/* row 4 */}
            <FormSelectInput
                inputGroupProps={{
                    label: "Do You Believe Home Can Be Repaired?",
                }}
                options={canBeHomeRepairedOptions}
                name={`${baseFieldName}.canBeRepaired`}
                control={control}
            />
            <FormSelectInput
                inputGroupProps={{
                    label: "Is New Housing/New Construction help needed?",
                }}
                options={isNewHousingHelpNeededOptions}
                name={`${baseFieldName}.newHousingHelpNeeded`}
                control={control}
            />
            <FormYesNoInput
                inputGroupProps={{
                    label: "Is help needed with Rebuilding?"
                }}
                name={`${baseFieldName}.rebuildingHelpNeeded`}
                control={control}
            />
            {/* row 5 */}
            <FormYesNoInput
                inputGroupProps={{
                    label: "Is help needed with Rebuilding supplies and materials?"
                }}
                name={`${baseFieldName}.rebuildingSuppliesHelpNeeded`}
                control={control}
            />
            <FormSelectInput
                inputGroupProps={{
                    label: "Height of Water in Home",
                }}
                options={heightOfWaterOptions}
                name={`${baseFieldName}.heightOfWater`}
                control={control}
            />
            <FormYesNoInput
                inputGroupProps={{
                    label: "Are there Dogs in the Home?",
                }}
                // options={yesNoOptions}
                name="home.dogsInHome"
                control={control}
            />
            <FormYesNoInput
                inputGroupProps={{
                    label: "Storage/Shed needed",
                }}
                name={"home.shedNeeded" as any}
                control={control}
            />
            {/* row 6 */}
            <FormSelectInput
                inputGroupProps={{
                    label: "What is the building height?",
                }}
                options={buildingHeightOptions}
                name={`${baseFieldName}.buildingHeight`}
                control={control}
            />
            <FormTextInput
                inputGroupProps={{
                    label: "What is the approx square feet of affected area of home?",
                }}
                placeholder="What is the approx square feet of affected area of home?"
                name={`${baseFieldName}.affectedSquareFeet`}
                type={"number"}
                control={control}
            />
            <FormSelectInput
                inputGroupProps={{
                    label: "Are there potential structural problems with the house?",
                }}
                options={potentialStructuralProblemsOptions}
                name={`${baseFieldName}.potentialStructuralProblems`}
                control={control}
            />
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{ label: "Please describe the structural problems", wrapperClassName: "three-cols" }}
                rows={4}
                name={`${baseFieldName}.structuralProblemDescription`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeTypeForm
